import type { Article, ArticleRevision, Book, Chapter } from "@/types/api";
import { request } from "./transport";

export interface CreatePersonalBookInput {
  title: string;
}

export interface UpdatePersonalBookInput {
  title?: string;
  subtitle?: string;
  preface?: string;
  cover_image_url?: string | null;
  sort_order?: number;
}

export interface CreatePersonalChapterInput {
  book_id: string;
  title?: string;
}

export interface UpdatePersonalChapterInput {
  title?: string;
  sort_order?: number;
}

export interface CreatePersonalArticleInput {
  chapter_id: string;
  title?: string;
  body_md?: string;
}

export interface UpdatePersonalArticleInput {
  title?: string;
  body_md?: string;
  sort_order?: number;
  chapter_id?: string;
  accent?: string;
}

/**
 * Shared shape for fork + save: pick an existing book and chapter, or
 * pass a name to auto-create one. Omit all four for the zero-friction
 * path that lands in the user's first book + sequentially named chapter.
 */
export interface PersonalTarget {
  book_id?: string;
  new_book_name?: string;
  chapter_id?: string;
  new_chapter_name?: string;
}

export interface ForkPersonalArticleInput extends PersonalTarget {
  source_article_id: string;
  /** "en" or "zh" — picks which body is copied. Defaults to "en". */
  locale?: "en" | "zh";
}

export interface SavePersonalArticleInput extends PersonalTarget {
  question: string;
  answer: string;
  history?: ReadonlyArray<{ question: string; answer: string }>;
  source_route?: string | null;
  source_locale?: string | null;
  source_model?: string | null;
}

export interface PersonalSaveResponse {
  article: Article;
  /** Set only when the save auto-created a brand-new book. */
  book: Book | null;
  /** Set only when the save auto-created a brand-new chapter. */
  chapter: Chapter | null;
}

const BASE = "/library/personal";

export const personalLibrary = {
  createPersonalBook: (input: CreatePersonalBookInput) =>
    request<{ book: Book; chapter: Chapter }>(`${BASE}/books`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  updatePersonalBook: (id: string, patch: UpdatePersonalBookInput) =>
    request<{ book: Book }>(`${BASE}/books/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deletePersonalBook: (id: string) =>
    request<{ ok: true }>(`${BASE}/books/${id}`, { method: "DELETE" }),

  createPersonalChapter: (input: CreatePersonalChapterInput) =>
    request<{ chapter: Chapter }>(`${BASE}/chapters`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  updatePersonalChapter: (id: string, patch: UpdatePersonalChapterInput) =>
    request<{ chapter: Chapter }>(`${BASE}/chapters/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deletePersonalChapter: (id: string) =>
    request<{ ok: true }>(`${BASE}/chapters/${id}`, { method: "DELETE" }),

  createPersonalArticle: (input: CreatePersonalArticleInput) =>
    request<{ article: Article }>(`${BASE}/articles`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  updatePersonalArticle: (id: string, patch: UpdatePersonalArticleInput) =>
    request<{ article: Article }>(`${BASE}/articles/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deletePersonalArticle: (id: string) =>
    request<{ ok: true }>(`${BASE}/articles/${id}`, { method: "DELETE" }),

  listPersonalArticleRevisions: (id: string) =>
    request<{ article_revisions: ArticleRevision[] }>(
      `${BASE}/articles/${id}/revisions`,
    ),

  restorePersonalArticleRevision: (articleId: string, revisionId: string) =>
    request<{ article: Article }>(
      `${BASE}/articles/${articleId}/revisions/${revisionId}/restore`,
      { method: "POST" },
    ),

  forkPersonalArticle: (input: ForkPersonalArticleInput) =>
    request<PersonalSaveResponse>(`${BASE}/articles/fork`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  saveToPersonalLibrary: (input: SavePersonalArticleInput) =>
    request<PersonalSaveResponse>(`${BASE}/save`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
};
